import React, { useState } from "react";
import { passwordlessAuthService } from "../services/firebase";
import { Dialog, Transition } from "@headlessui/react";
import { sanitizeEmail } from "../utils/inputSanitization";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens the sign-up form; sign-in links only work for emails that have an account. */
  onRequestInvite?: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose, onRequestInvite }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    // Sanitize and validate email
    const sanitizedEmail = sanitizeEmail(email);
    if (!sanitizedEmail) {
      setError("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }

    try {
      await passwordlessAuthService.sendSignInLink(sanitizedEmail);
      setSuccess("Check your email for the sign-in link. You can close this window.");
    } catch (err) {
      console.error("Error sending sign-in link:", err);
      setError(
        err instanceof Error ? err.message : "Failed to send sign-in link. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 flex items-center justify-center bg-[color-mix(in_srgb,var(--color-bg)_60%,transparent)] backdrop-blur-sm"
        onClose={onClose}
        static
      >
        <div className="min-h-screen px-2 flex items-center justify-center w-full">
          <Dialog.Panel className="w-full max-w-sm rounded-[var(--radius-lg)] bg-[var(--color-bg-secondary)] p-0 overflow-hidden border border-[var(--color-border)] text-[var(--color-text-primary)]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
              <Dialog.Title
                as="h3"
                className="text-base font-medium text-[var(--color-text-primary)]"
              >
                Sign In
              </Dialog.Title>
              <button
                onClick={onClose}
                className="rounded-md p-1 hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                <span className="sr-only">Close</span>
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-5 h-5 text-[var(--color-text-tertiary)]"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414l4.95-4.95-4.95-4.95A1 1 0 015.05 3.636l4.95 4.95z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <form className="px-5 py-6 flex flex-col gap-4" onSubmit={handleSubmit}>
              <p className="text-xs text-[var(--color-text-tertiary)]">
                We'll email you a one-time sign-in link, no password needed.
                {onRequestInvite && (
                  <>
                    {" New here with an invite code? "}
                    <button
                      type="button"
                      className="text-[var(--color-accent)] hover:underline focus:outline-none"
                      onClick={onRequestInvite}
                    >
                      Create an account
                    </button>
                    {" first."}
                  </>
                )}
              </p>
              <input
                type="email"
                className="px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              {error && <div className="text-xs text-[var(--color-danger)]">{error}</div>}
              {success && <div className="text-xs text-[var(--color-success)]">{success}</div>}
              <button
                type="submit"
                className={`mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]
                  ${
                    isLoading
                      ? "bg-[var(--color-bg-hover)] text-[var(--color-text-tertiary)] cursor-not-allowed"
                      : "bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-text-inverse)]"
                  }
                `}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Sign-in Link"}
              </button>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SignInModal;
